import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Grid,
  Paper,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { useFormik, FormikProvider } from "formik";
import { toast } from "react-toastify";

import { SmallQuantityTicket } from "../../interface/api/SmallQuantityTicket";
import { ContractorCompany } from "../../interface/api/ContractorCompany";
import { ContractorCompaniesWithThresholdsResponse } from "../../interface/api/ContractorCompaniesWithThresholdsResponse";
import { Load } from "../../interface/api/soilflo/Load";
import { Truck } from "../../interface/api/Truck";
import smallQuantityTicketService from "../../services/smallQuantityTicketService";
import ticketService from "../../services/ticketService";
import contractorCompanyService from "../../services/contractorCompanyService";
import truckService from "../../services/truckService";
import PageHero from "../shared/page-hero";
import LoadingView from "../shared/loading-view";
import { SoilFLOIcon } from "../shared/icons";
import { useTicketCrud } from "../../hooks/useTicketCrud";
import { useAppSelector } from "../../hooks";
import { getCurrentUser } from "../../store/userStore";
import { history } from "../..";
import { formatDate, removeSeconds } from "../../utils/date-helper";
import FormSelectSearchField from "../shared/form-select-search-field";
import FormDateTimePicker from "../shared/form-date-time-picker";
import { useSoilFLOStyles } from "../../utils/soilflo-styles";
import PriceField from "../shared/price-field";
import { PaymentMethods } from "../../utils/constants";
import contractorProjectService from "../../services/contractorProjectService";
import { Project } from "../../interface/api/Project";
import JobSiteField from "../shared/job-site-field";
import { useTransportCascade } from "../../hooks/useTransportCascade";
import FormTruckLicensePlateField from "../shared/form-truck-license-plate-field";
import FormSelect from "../shared/form-select";
import LicensePlateAutoLookup from "../shared/LicensePlateAutoLookup";

const SmallQuantityTicketView = () => {
  const classes = useSoilFLOStyles();
  const { id } = useParams<{ id: string }>();
  const user = useAppSelector(getCurrentUser);
  const { ticket, loading, loadTicket } =
    useTicketCrud<SmallQuantityTicket>(id, smallQuantityTicketService);
  const [contractors, setContractors] = useState<
    { value: number; label: string }[]
  >([]);
  const [miscContractor, setMiscContractor] = useState<ContractorCompany | null>(null);
  const [isMisc, setIsMisc] = useState(false);
  const prevContractorRef = useRef<{
    contractorCompanyId: number | null;
    contractorCompanyName: string | null;
    projectId: number | null;
    projectProjectId: string | null;
    jobSite: string;
  } | null>(null);
  const [soilFLOLoad, setSoilFLOLoad] = useState<Load | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [allTrucks, setAllTrucks] = useState<Truck[]>([]);

  const formik = useFormik<SmallQuantityTicket>({
    initialValues: (ticket ?? {}) as SmallQuantityTicket,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  const formikSetTicket: React.Dispatch<React.SetStateAction<SmallQuantityTicket | null>> = (action) => {
    if (typeof action === "function") {
      formik.setValues((prev) => action(prev as SmallQuantityTicket) ?? prev);
    } else if (action !== null) {
      formik.setValues(action);
    }
  };

  const { transportCompanies, trucks, onTransportCompanyChange, onTruckChange } =
    useTransportCascade({ ticket: formik.values, setTicket: formikSetTicket });

  useEffect(() => {
    contractorCompanyService.getContractorCompanies().then((response) => {
      if (response.ok && response.data) {
        const data = response.data as ContractorCompaniesWithThresholdsResponse;
        const companies = data.contractorCompanies.filter(
          (c) => c.isSmallQuantity
        );
        const misc = companies.find((c) => c.isMiscellaneous) ?? null;
        setMiscContractor(misc);
        setContractors(
          companies.filter((c) => !c.isMiscellaneous).map((c) => ({ value: c.id, label: c.name }))
        );
      }
    });
    truckService
      .getTrucks({ transportCompanyId: null, truck: null, transportContact: null })
      .then((response) => {
        if (response.ok && response.data) {
          setAllTrucks(response.data as Truck[]);
        }
      });
  }, []);

  useEffect(() => {
    if (ticket && miscContractor) {
      setIsMisc(ticket.contractorCompanyId === miscContractor.id);
    }
  }, [ticket?.id, miscContractor]);

  // Load projects when contractor changes
  useEffect(() => {
    if (formik.values?.contractorCompanyId) {
      contractorProjectService
        .getContractorProjects(formik.values.contractorCompanyId)
        .then((response) => {
          if (response.ok) {
            setProjects(response.data as Project[]);
          }
        });
    } else {
      setProjects([]);
    }
  }, [formik.values?.contractorCompanyId]);

  // Fetch SoilFLO data and map into ticket fields
  useEffect(() => {
    if (ticket?.isSoilFLOLoad) {
      ticketService.getLoad(ticket.ticketNumber.toString()).then((response) => {
        if (response.ok) {
          const data = response.data as Load[];
          if (data && data.length > 0) {
            const load = data.sort((a, b) => b.id - a.id)[0];
            setSoilFLOLoad(load);
            formik.setValues((prev) => ({
              ...prev,
              jobSite: load.sourceSite?.name ?? prev.jobSite,
              contractorCompanyName:
                load.sourceSite?.contractingcompany?.name ??
                prev.contractorCompanyName,
              haulageCompanyName:
                load.truck?.global_truck?.hauler?.name ??
                prev.haulageCompanyName,
              truckNumber:
                load.truck?.global_truck?.number ?? prev.truckNumber,
              truckLicensePlate:
                load.truck?.global_truck?.license ?? prev.truckLicensePlate,
              truckDriverName: load.truck?.driverName ?? prev.truckDriverName,
              timeLoaded: load.dispatchedTime ?? prev.timeLoaded,
            }));
          }
        }
      });
    }
  }, [ticket?.id]);

  const isSoilFLO = ticket?.isSoilFLOLoad && soilFLOLoad != null;

  const onUnreceive = async () => {
    if (!ticket) return;
    const response = await smallQuantityTicketService.unreceiveTicket(ticket.id);
    if (response.ok) {
      toast.success("Ticket unreceived");
      loadTicket();
    } else {
      toast.error("Failed to unreceive ticket");
    }
  };

  const onSaveTicket = async () => {
    if (!formik.values) return;
    const _ticket = { ...formik.values };
    _ticket.timeLoaded = removeSeconds(_ticket.timeLoaded);
    _ticket.timeReceived = removeSeconds(_ticket.timeReceived);
    const response = await smallQuantityTicketService.saveTicket(_ticket);
    if (response.ok) {
      toast.success("Ticket saved");
      loadTicket();
    } else {
      toast.error("Failed to save ticket");
    }
  };

  return (
    <LoadingView loading={loading} data={ticket} notFoundMessage="Ticket not found">
      {ticket && (
        <FormikProvider value={formik}>
          <LicensePlateAutoLookup
            ticket={formik.values}
            setTicket={(t) => formik.setValues(t)}
            trucks={allTrucks}
          />
          <Container maxWidth="sm">
            <PageHero
              title=""
              subtitle=""
              showBackButton={true}
            />
            {ticket.isReceived && (
              <Box display="flex" alignItems="center" mt={2} mb={1}>
                <Chip label="Received" color="primary" style={{ marginRight: 12 }} />
                <Button variant="outlined" color="secondary" size="small" onClick={onUnreceive}>
                  Unreceive
                </Button>
              </Box>
            )}
            {isSoilFLO && (
              <Box display="flex" alignItems="center" mt={2} mb={1} style={{ gap: 8 }}>
                <SoilFLOIcon />
                <Typography variant="body2" color="textSecondary">
                  Fields pulled from SoilFLO are read-only
                </Typography>
              </Box>
            )}
            <Paper style={{ padding: 24, marginTop: 16 }}>
              {/* Top section: Date, Contractor, Job Site */}
              <Grid container spacing={2}>
                {!isSoilFLO && miscContractor && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isMisc}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setIsMisc(checked);
                            if (checked) {
                              prevContractorRef.current = {
                                contractorCompanyId: formik.values.contractorCompanyId,
                                contractorCompanyName: formik.values.contractorCompanyName,
                                projectId: formik.values.projectId,
                                projectProjectId: formik.values.projectProjectId,
                                jobSite: formik.values.jobSite ?? "",
                              };
                              formik.setValues({
                                ...formik.values,
                                contractorCompanyId: miscContractor.id,
                                contractorCompanyName: "",
                                projectId: null,
                                projectProjectId: null,
                                jobSite: "",
                              });
                            } else {
                              const prev = prevContractorRef.current;
                              formik.setValues({
                                ...formik.values,
                                contractorCompanyId: prev?.contractorCompanyId ?? null,
                                contractorCompanyName: prev?.contractorCompanyName ?? null,
                                projectId: prev?.projectId ?? null,
                                projectProjectId: prev?.projectProjectId ?? null,
                                jobSite: prev?.jobSite ?? "",
                              });
                              prevContractorRef.current = null;
                            }
                          }}
                          color="primary"
                        />
                      }
                      label="Miscellaneous Company"
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  {isSoilFLO ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Contractor"
                      value={formik.values.contractorCompanyName ?? ""}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      className={classes.soilFLOField}
                    />
                  ) : isMisc ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Company Name"
                      value={formik.values.contractorCompanyName ?? ""}
                      onChange={(e) => formik.setFieldValue("contractorCompanyName", e.target.value)}
                    />
                  ) : (
                    <FormSelectSearchField
                      name="contractorCompanyId"
                      placeholder="Contractor"
                      options={contractors}
                      value={
                        formik.values.contractorCompanyId
                          ? contractors.find(
                              (c) => c.value === formik.values.contractorCompanyId
                            ) ?? null
                          : null
                      }
                      onChange={(option: any) => {
                        if (option) {
                          formik.setValues({
                            ...formik.values,
                            contractorCompanyId: option.value,
                            contractorCompanyName: option.label,
                          });
                        } else {
                          formik.setValues({
                            ...formik.values,
                            contractorCompanyId: null,
                            contractorCompanyName: null,
                          });
                        }
                      }}
                      isClearable
                    />
                  )}
                </Grid>
                {!isMisc && (
                <>
                <Grid item xs={12}>
                  {isSoilFLO ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Project"
                      value={
                        formik.values.projectId
                          ? `${formik.values.projectProjectId ?? ""} — ${formik.values.jobSite ?? ""}`
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      className={classes.soilFLOField}
                    />
                  ) : (
                    <FormSelectSearchField
                      name="projectId"
                      placeholder="Project"
                      options={projects.map((p) => ({
                        value: p.id,
                        label: `${p.projectId} — ${p.jobSite}`,
                      }))}
                      value={
                        formik.values.projectId
                          ? projects
                              .map((p) => ({
                                value: p.id,
                                label: `${p.projectId} — ${p.jobSite}`,
                              }))
                              .find((opt) => opt.value === formik.values.projectId) ?? null
                          : null
                      }
                      onChange={(option: any) => {
                        if (option) {
                          const project = projects.find((p) => p.id === option.value);
                          formik.setValues({
                            ...formik.values,
                            projectId: option.value,
                            projectProjectId: project?.projectId ?? null,
                            jobSite: project?.jobSite ?? formik.values.jobSite,
                          });
                        } else {
                          formik.setValues({
                            ...formik.values,
                            projectId: null,
                            projectProjectId: null,
                            jobSite: "",
                          });
                        }
                      }}
                      isClearable
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <JobSiteField
                    label="Job Site"
                    value={formik.values.jobSite ?? ""}
                    items={projects.map((p) => p.jobSite)}
                    onChange={(val) => formik.setFieldValue("jobSite", val)}
                    readOnly={isSoilFLO}
                    className={isSoilFLO ? classes.soilFLOField : undefined}
                  />
                </Grid>
                </>
                )}
              </Grid>

              {/* 9th LINE FILL SITE */}
              <Typography
                variant="subtitle1"
                style={{ textAlign: "center", fontWeight: 600, marginTop: 20, marginBottom: 8 }}
              >
                9th LINE FILL SITE
              </Typography>

              {/* LOADING SITE */}
              <Box
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <Typography
                  variant="h6"
                  style={{ fontWeight: 600, textDecoration: "underline" }}
                >
                  LOADING SITE
                </Typography>
                <Typography variant="h6" style={{ color: "red", fontWeight: 600 }}>
                  {ticket.ticketNumber}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                {ticket.isReceived
                  ? (ticket.timeReceived ? formatDate(ticket.timeReceived, false) : "—")
                  : (ticket.timeLoaded ? formatDate(ticket.timeLoaded, false) : "—")}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <div className={isSoilFLO ? classes.soilFLOField : undefined}>
                    <FormDateTimePicker
                      label="Time Loaded"
                      value={formik.values.timeLoaded ? new Date(formik.values.timeLoaded) : null}
                      onChange={(date: any) =>
                        formik.setFieldValue("timeLoaded", date)
                      }
                      readOnly={isSoilFLO}
                      clearable={!isSoilFLO}
                    />
                  </div>
                </Grid>
                <Grid item xs={12}>
                  {isSoilFLO ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Haulage Co."
                      value={formik.values.haulageCompanyName ?? ""}
                      InputProps={{ readOnly: true }}
                      className={classes.soilFLOField}
                    />
                  ) : (
                    <FormSelectSearchField
                      name="transportCompanyId"
                      placeholder="Select Haulage Co."
                      value={
                        formik.values.transportCompanyId
                          ? transportCompanies.find(
                              (c) => c.value === formik.values.transportCompanyId
                            ) ?? (formik.values.transportCompanyName
                              ? { value: formik.values.transportCompanyId, label: formik.values.transportCompanyName }
                              : null)
                          : null
                      }
                      options={transportCompanies}
                      onChange={onTransportCompanyChange}
                      isClearable
                    />
                  )}
                </Grid>
                <Grid item xs={6}>
                  {isSoilFLO ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Truck Number"
                      value={formik.values.truckNumber ?? ""}
                      InputProps={{ readOnly: true }}
                      className={classes.soilFLOField}
                    />
                  ) : (
                    <FormSelect
                      name="truckId"
                      label="Truck Number"
                      value={formik.values.truckId}
                      onChange={onTruckChange}
                      items={[
                        trucks.map((truck) => (
                          <MenuItem key={truck.id} value={truck.id}>
                            {truck.number}
                          </MenuItem>
                        )),
                      ]}
                    />
                  )}
                </Grid>
                <Grid item xs={6}>
                  {isSoilFLO ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Truck License Plate"
                      value={formik.values.truckLicensePlate?.toUpperCase() ?? ""}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      className={classes.soilFLOField}
                    />
                  ) : (
                    <FormTruckLicensePlateField
                      value={formik.values.truckLicensePlate ?? ""}
                      name="truckLicensePlate"
                      label="Truck License Plate"
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  {isSoilFLO ? (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Driver Name"
                      value={formik.values.truckDriverName ?? ""}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      className={classes.soilFLOField}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Driver Name"
                      value={formik.values.truckDriverName ?? ""}
                      onChange={(e) =>
                        formik.setFieldValue("truckDriverName", e.target.value)
                      }
                    />
                  )}
                </Grid>
              </Grid>

              {/* NO REFUND banner */}
              <Typography
                variant="subtitle1"
                style={{
                  fontWeight: 600,
                  textAlign: "center",
                  color: "red",
                  marginTop: 20,
                  marginBottom: 4,
                }}
              >
                NO REFUND - NOT TRANSFERABLE
              </Typography>
              <div style={{ border: "1px solid black", marginBottom: 16 }} />

              {/* RECEIVING SITE */}
              <Typography
                variant="h6"
                style={{
                  fontWeight: 600,
                  textDecoration: "underline",
                  marginBottom: 12,
                }}
              >
                RECEIVING SITE
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormDateTimePicker
                    label="Time Received"
                    value={formik.values.timeReceived ? new Date(formik.values.timeReceived) : null}
                    onChange={(date: any) =>
                      formik.setFieldValue("timeReceived", date)
                    }
                    clearable
                  />
                </Grid>
              </Grid>

              {/* Tear-off separator */}
              <div style={{ border: "1px dashed black", marginTop: 20, marginBottom: 20 }} />

              {/* Bottom stub */}
              <Typography variant="h6" style={{ marginBottom: 4 }}>
                <b>UNITED</b> Soils Management Ltd.
              </Typography>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>
                SMALL QUANTITIES
              </Typography>
              <Typography style={{ marginBottom: 4 }}>
                <b>Contractor:</b> {formik.values.contractorCompanyName ?? "—"}
              </Typography>
              <Typography style={{ marginBottom: 4 }}>
                <b>Job Site:</b> {formik.values.jobSite ?? "—"}
              </Typography>
              <Box
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 8,
                }}
              >
                <Typography style={{ fontWeight: 600 }}>9th LINE FILL SITE</Typography>
                <Typography variant="h6" style={{ color: "red", fontWeight: 600 }}>
                  {ticket.ticketNumber}
                </Typography>
              </Box>

              {/* Additional fields */}
              <div style={{ border: "1px solid #ccc", marginTop: 20, marginBottom: 20 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      label="Payment Method"
                      value={formik.values.paymentMethod ?? ""}
                      onChange={(e) => formik.setFieldValue("paymentMethod", e.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {PaymentMethods.map((m) => (
                        <MenuItem key={m} value={m}>{m}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PriceField
                    label="Sale Price"
                    value={formik.values.salePrice}
                    onChange={(val) => formik.setFieldValue("salePrice", val)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={3}
                    label="Notes"
                    value={formik.values.notes ?? ""}
                    onChange={(e) => formik.setFieldValue("notes", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button variant="contained" color="primary" onClick={onSaveTicket}>
                      Save
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Container>
        </FormikProvider>
      )}
    </LoadingView>
  );
};

export default SmallQuantityTicketView;
